import React, { useState, useRef, useEffect } from 'react';
import { CanvasElement } from '../types/elementRenderer';

interface TextEditorProps {
  element: CanvasElement;
  onComplete: (id: string, payload: { content: string; fontFamily: string; fontSize: number }) => void;
  onCancel: () => void;
}

const FONT_FAMILIES = [
  'Arial', 'Inter', 'Roboto', 'Segoe UI', 'Tahoma', 'Verdana',
  'Times New Roman', 'Georgia', 'Courier New', 'Monaco',
];

/** SVG inline text editor rendered via <foreignObject>. Supports multi-line content split by \n. */
const TextEditor: React.FC<TextEditorProps> = ({ element, onComplete, onCancel }) => {
  const initialLines = (element.content || '').split('\n');
  const [lines, setLines] = useState<string[]>(initialLines);
  const [fontFamily, setFontFamily] = useState(element.fontFamily || 'Arial');
  const [fontSize, setFontSize] = useState(() => Math.min(element.fontSize || 16, 96));
  const firstInputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => { firstInputRef.current?.focus(); }, []);

  const commit = () => {
    onComplete(element.id, {
      content: lines.join('\n'),
      fontFamily,
      fontSize: Math.max(8, Math.min(fontSize, 96)),
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    else if (e.key === 'Escape') onCancel();
  };

  const handleRootBlur = (e: React.FocusEvent) => {
    const next = e.relatedTarget as Node | null;
    if (rootRef.current && next && rootRef.current.contains(next)) return;
    commit();
  };

  const fs = Math.max(8, Math.min(fontSize, 96));
  const maxLen = Math.max(...lines.map(l => l.length), 8);
  const width = Math.max(200, maxLen * fs * 0.65);
  const inputH = fs * 2.2;
  const totalH = 40 + lines.length * inputH + (lines.length - 1) * 6 + 12;

  // For text elements x,y is the center point
  const x = element.x - width / 2;
  const y = element.y - totalH / 2;

  const inputStyle: React.CSSProperties = {
    fontSize: fs,
    fontFamily,
    color: element.fill || '#000',
    background: 'white',
    border: '2px solid #007ACC',
    borderRadius: 2,
    padding: '4px 6px',
    width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <foreignObject x={x} y={y} width={width} height={totalH} style={{ overflow: 'visible' }}>
      <div
        // @ts-expect-error xmlns needed for SVG foreignObject
        xmlns="http://www.w3.org/1999/xhtml"
        ref={rootRef}
        onBlur={handleRootBlur}
        tabIndex={-1}
        style={{
          display: 'flex', flexDirection: 'column', gap: 6, width: '100%',
          background: 'white', borderRadius: 4, padding: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)', boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', gap: 6 }}>
          <select
            value={fontFamily}
            onChange={(e) => setFontFamily(e.target.value)}
            style={{ flex: 1, padding: '2px 4px' }}
          >
            {FONT_FAMILIES.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <input
            type="number" min={8} max={96} value={fontSize}
            onChange={(e) => setFontSize(Number(e.target.value))}
            style={{ width: 70, padding: '2px 4px' }}
          />
        </div>
        {lines.map((line, i) => (
          <input
            key={`line-${i}-${line.slice(0, 20)}`}
            ref={i === 0 ? firstInputRef : null}
            type="text"
            value={line}
            onChange={(e) => { const nl = [...lines]; nl[i] = e.target.value; setLines(nl); }}
            onKeyDown={handleKeyDown}
            style={inputStyle}
          />
        ))}
      </div>
    </foreignObject>
  );
};

export default TextEditor;
